/**
 * CompanyFeaturesModal Component
 *
 * Modal for managing features of an existing company.
 * Displays features grouped by category with enable/disable toggles
 * and limit configuration.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  Settings,
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Save,
  RefreshCw,
  Zap,
} from 'lucide-react';
import {
  superAdminApi,
  AdminCompany,
  CompanyFeature,
  CompanyFeaturesResponse,
  FeatureCategory,
  LimitPeriod,
  BatchUpdateFeatureItem,
} from '../../services/adminApi';

interface CompanyFeaturesModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: AdminCompany | null;
  onSave?: () => void;
}

interface FeatureEditState {
  is_enabled: boolean;
  limit_value?: number;
  limit_period?: LimitPeriod;
  isDirty: boolean;
}

const CATEGORY_ICONS: Record<FeatureCategory, string> = {
  core: '🔧',
  advanced: '⚡',
  export: '📤',
  monitoring: '📡',
  api: '🔌',
};

const CATEGORY_LABELS: Record<FeatureCategory, string> = {
  core: 'Core Features',
  advanced: 'Advanced Features',
  export: 'Export Features',
  monitoring: 'Monitoring',
  api: 'API Access',
};

const CATEGORY_ORDER: FeatureCategory[] = ['core', 'advanced', 'export', 'monitoring', 'api'];

export const CompanyFeaturesModal: React.FC<CompanyFeaturesModalProps> = ({
  isOpen,
  onClose,
  company,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [featuresData, setFeaturesData] = useState<CompanyFeaturesResponse | null>(null);
  const [editStates, setEditStates] = useState<Record<string, FeatureEditState>>({});
  const [expandedCategories, setExpandedCategories] = useState<Set<FeatureCategory>>(
    new Set(CATEGORY_ORDER)
  );

  // Fetch company features when modal opens
  useEffect(() => {
    if (isOpen && company) {
      fetchCompanyFeatures();
    }
  }, [isOpen, company]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSuccessMessage(null);
      setFeaturesData(null);
      setEditStates({});
    }
  }, [isOpen]);

  const fetchCompanyFeatures = async () => {
    if (!company) return;

    setLoading(true);
    setError(null);
    try {
      const data = await superAdminApi.getCompanyFeatures(company.id);
      setFeaturesData(data);

      // Initialize edit states from features
      const initialStates: Record<string, FeatureEditState> = {};
      data.features.forEach((feature) => {
        initialStates[feature.feature_key] = {
          is_enabled: feature.is_enabled,
          limit_value: feature.limit_value,
          limit_period: feature.limit_period,
          isDirty: false,
        };
      });
      setEditStates(initialStates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch features');
    } finally {
      setLoading(false);
    }
  };

  // Group features by category
  const featuresByCategory = useMemo(() => {
    if (!featuresData) return {};

    const grouped: Record<FeatureCategory, CompanyFeature[]> = {
      core: [],
      advanced: [],
      export: [],
      monitoring: [],
      api: [],
    };

    featuresData.features.forEach((feature) => {
      if (grouped[feature.category]) {
        grouped[feature.category].push(feature);
      }
    });

    return grouped;
  }, [featuresData]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return Object.values(editStates).some((state) => state.isDirty);
  }, [editStates]);

  // Get counts
  const enabledCount = useMemo(() => {
    return Object.values(editStates).filter((state) => state.is_enabled).length;
  }, [editStates]);

  const totalCount = featuresData?.features.length || 0;

  const toggleCategory = (category: FeatureCategory) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const toggleFeature = (featureKey: string) => {
    setEditStates((prev) => {
      const current = prev[featureKey];
      if (!current) return prev;

      return {
        ...prev,
        [featureKey]: {
          ...current,
          is_enabled: !current.is_enabled,
          isDirty: true,
        },
      };
    });
    setSuccessMessage(null);
  };

  const updateFeatureLimit = (
    featureKey: string,
    field: 'limit_value' | 'limit_period',
    value: number | LimitPeriod
  ) => {
    setEditStates((prev) => {
      const current = prev[featureKey];
      if (!current) return prev;

      return {
        ...prev,
        [featureKey]: {
          ...current,
          [field]: value,
          isDirty: true,
        },
      };
    });
    setSuccessMessage(null);
  };

  const getCategoryEnabledCount = (category: FeatureCategory): number => {
    const features = featuresByCategory[category] || [];
    return features.filter((f) => editStates[f.feature_key]?.is_enabled).length;
  };

  const handleSave = async () => {
    if (!company) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Build features array from dirty states
      const features: BatchUpdateFeatureItem[] = Object.entries(editStates)
        .filter(([, state]) => state.isDirty)
        .map(([featureKey, state]) => ({
          feature_key: featureKey,
          is_enabled: state.is_enabled,
          limit_value: state.limit_value,
          limit_period: state.limit_period,
        }));

      if (features.length === 0) {
        setSuccessMessage('No changes to save');
        setSaving(false);
        return;
      }

      await superAdminApi.batchUpdateCompanyFeatures(company.id, { features });

      // Reset dirty flags
      setEditStates((prev) => {
        const newStates = { ...prev };
        Object.keys(newStates).forEach((key) => {
          newStates[key] = { ...newStates[key], isDirty: false };
        });
        return newStates;
      });

      setSuccessMessage(`Successfully updated ${features.length} feature(s)`);
      if (onSave) onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const formatPeriod = (period?: LimitPeriod): string => {
    switch (period) {
      case 'daily':
        return 'day';
      case 'weekly':
        return 'week';
      case 'monthly':
        return 'month';
      case 'yearly':
        return 'year';
      default:
        return 'month';
    }
  };

  const getUsagePercentage = (feature: CompanyFeature): number => {
    if (!feature.limit_value || !feature.current_usage) return 0;
    return Math.round((feature.current_usage / feature.limit_value) * 100);
  };

  const getUsageColor = (percentage: number): string => {
    if (percentage >= 90) return 'text-red-600 bg-red-100';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[var(--z-modal)] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-3xl transform rounded-2xl bg-white shadow-xl transition-all max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-600" />
                Manage Features - {company?.name}
              </h3>
              {featuresData && (
                <p className="text-sm text-gray-600 mt-1">
                  {enabledCount}/{totalCount} features enabled
                  {hasChanges && (
                    <span className="ml-2 text-orange-600 font-medium">
                      (unsaved changes)
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchCompanyFeatures}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700">{successMessage}</p>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            )}

            {/* Features List */}
            {!loading && featuresData && (
              <div className="space-y-4">
                {CATEGORY_ORDER.map((category) => {
                  const categoryFeatures = featuresByCategory[category];
                  if (!categoryFeatures || categoryFeatures.length === 0) return null;

                  const isExpanded = expandedCategories.has(category);
                  const enabledInCategory = getCategoryEnabledCount(category);
                  const totalInCategory = categoryFeatures.length;

                  return (
                    <div
                      key={category}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(category)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{CATEGORY_ICONS[category]}</span>
                          <span className="font-medium text-gray-900">
                            {CATEGORY_LABELS[category]}
                          </span>
                          <span className="text-sm text-gray-500">
                            ({enabledInCategory}/{totalInCategory} enabled)
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </button>

                      {/* Category Features */}
                      {isExpanded && (
                        <div className="divide-y divide-gray-100">
                          {categoryFeatures.map((feature) => {
                            const editState = editStates[feature.feature_key];
                            if (!editState) return null;

                            const usagePercentage = getUsagePercentage(feature);

                            return (
                              <div
                                key={feature.feature_key}
                                className={`px-4 py-3 hover:bg-gray-50 ${
                                  editState.isDirty ? 'bg-orange-50' : ''
                                }`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-gray-900">
                                        {feature.feature_name}
                                      </span>
                                      {editState.isDirty && (
                                        <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
                                          Modified
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {feature.feature_description}
                                    </p>

                                    {/* Usage Display */}
                                    {feature.has_limit && editState.is_enabled && feature.current_usage !== undefined && (
                                      <div className="mt-2 flex items-center gap-2">
                                        <Zap className="w-3 h-3 text-gray-400" />
                                        <span className="text-xs text-gray-600">
                                          Usage: {feature.current_usage || 0}/{editState.limit_value || feature.limit_value || '?'}
                                          {' '}per {formatPeriod(editState.limit_period || feature.limit_period)}
                                        </span>
                                        <span
                                          className={`text-xs px-1.5 py-0.5 rounded ${getUsageColor(usagePercentage)}`}
                                        >
                                          {usagePercentage}%
                                        </span>
                                      </div>
                                    )}

                                    {/* Limit Configuration */}
                                    {feature.has_limit && editState.is_enabled && (
                                      <div className="flex items-center gap-2 mt-2">
                                        <span className="text-xs text-gray-500">Limit:</span>
                                        <input
                                          type="number"
                                          value={editState.limit_value || ''}
                                          onChange={(e) =>
                                            updateFeatureLimit(
                                              feature.feature_key,
                                              'limit_value',
                                              parseInt(e.target.value, 10) || 0
                                            )
                                          }
                                          placeholder="Value"
                                          min="1"
                                          className="w-20 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                                        />
                                        <select
                                          value={editState.limit_period || 'monthly'}
                                          onChange={(e) =>
                                            updateFeatureLimit(
                                              feature.feature_key,
                                              'limit_period',
                                              e.target.value as LimitPeriod
                                            )
                                          }
                                          className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white"
                                        >
                                          <option value="daily">per day</option>
                                          <option value="weekly">per week</option>
                                          <option value="monthly">per month</option>
                                          <option value="yearly">per year</option>
                                        </select>
                                      </div>
                                    )}
                                  </div>

                                  {/* Toggle Switch */}
                                  <button
                                    onClick={() => toggleFeature(feature.feature_key)}
                                    className={`
                                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                                      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                                      ${editState.is_enabled ? 'bg-purple-600' : 'bg-gray-200'}
                                    `}
                                    role="switch"
                                    aria-checked={editState.is_enabled}
                                  >
                                    <span
                                      className={`
                                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                                        transition duration-200 ease-in-out
                                        ${editState.is_enabled ? 'translate-x-5' : 'translate-x-0'}
                                      `}
                                    />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {featuresData && (
                  <>
                    <span className="font-medium">{enabledCount}</span> of{' '}
                    <span className="font-medium">{totalCount}</span> features enabled
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className={`
                    px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2
                    ${
                      hasChanges
                        ? 'bg-purple-600 hover:bg-purple-700'
                        : 'bg-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyFeaturesModal;
